<template>
    <div class="card" style="min-height: calc(100vh - 158px)">
        <div class="p-2 bg-blue-light">
            <TitleButton
                :showBtn="false"
                title="Analytics of profit and loss"
                :show-filter-btn="true"
                @onClickFilterButton="onClickFilterButton"
            />

            <DateQuerySetter class="mt-2" btnTitle="Go" @onClickGo="onClickGo">
                <div class="cols-12 col-sm-6 col-lg-2">
                    <label class="col-form-label">Column By</label>
                    <v-select
                        placeholder="Select Period"
                        v-model="columnBy"
                        :options="columns"
                        label="name"
                        :reduce="name => name.key"
                    />
                </div>
                <div class="cols-12 col-sm-6 col-lg-2" v-if="columnBy === 'period'">
                    <label class="col-form-label">Period</label>
                    <v-select
                        placeholder="Select Period"
                        v-model="period"
                        :options="periods"
                        label="name"
                        :reduce="name => name.key"
                    />
                </div>
                <div class="col-12 col-sm-6 col-md-6 col-lg-2">
                    <label class="col-form-label">Business</label>
                    <v-select
                        placeholder="Select Business"
                        class="w-100"
                        :options="businesses"
                        label="name"
                        :reduce="(name) => name.id"
                        v-model="businessId"
                    />
                </div>
                <div class="cols-12 col-sm-6 col-lg-4 col-xxl-2">
                    <label class="col-form-label">Project</label>
                    <v-select
                        placeholder="Select Project"
                        v-model="projectId"
                        :options="projects"
                        label="name"
                        :reduce="name => name.id"
                    />
                </div>
                <div class="col-12 col-sm-6 col-md-6 col-lg-2">
                    <label class="col-form-label">Cost Centre</label>
                    <v-select
                        placeholder="Select Cost Centre"
                        v-model="costCentreId"
                        :options="costCentres"
                        label="name"
                        :reduce="name => name.id"
                    />
                </div>
                <div class="col-12 col-sm-6 col-md-6 col-lg-2">
                    <div class="d-flex align-items-center justify-content-between filter-label">
                        <label class="col-form-label">Party: </label>
                        <span
                            class="filter-search-icon cursor-pointer"
                            @click="onOpenContactSearchModal"
                        >
                          <i class="fas fa-search"></i>
                        </span>
                    </div>

                    <AsyncSelect
                        placeholder="Select Party"
                        v-model="contactProfile"
                        :api-service="fetchContactProfiles"
                        :format-label="formatPatientLabel"
                    />
                </div>
            </DateQuerySetter>
            <div class="d-flex align-items-center justify-content-end">
                <button class="btn btn-success float-end me-4" @click="exportProfitLoss">Export</button>
            </div>
        </div>

        <div class="px-2">
            <ProfitLossAnalyticsTable
                :sections="[
                { title: 'Revenue', key:'revenue', data: revenue },
                { title: 'Cost of Sales', key:'cost_of_sales', data: costOfSales },
                { title: 'Operating Expenses', key:'operating_expenses', data: operatingExpenses },
                { title: 'Other Income', key:'other_income', data: otherIncome },
                { title: 'Other Expense', key:'other_expense', data: otherExpense }
            ]"
                :columns="reportPeriod"
                :noOfDecimals="noOfDecimals"
                :showAccountCode="showAccountCode"
                :grossProfit="grossProfit"
                :operatingProfit="operatingProfit"
                :netProfit="netProfit"
                :columnGrossProfit="columnGrossProfit"
                :columnOperatingProfit="columnOperatingProfit"
                :columnNetProfit="columnNetProfit"
            />
        </div>

        <Loader v-if="accountingLoading" />
        <ContactDropdownFilter
            v-if="store.state.isModalOpenThree"
            @onSearchContact="onSearchContact"
            :isFilterable="true"
            :companyRoles="companyRoles"
        />
        <FilterModal v-if="isModalOpen" @on-filter="onFilter" />
    </div>
</template>

<script>
import { inject } from "vue";
import { useStore } from "vuex";
import figureFormatter from "@/services/utils/figureFormatter";
import handleAccounting from "@/services/modules/accounting";
import handleCBusinesses from "@/services/modules/businesses";
import handleProjects from "@/services/modules/procurement/project";
import handleCostCentres from "@/services/modules/ngo/costCentre";
import handleContact from "@/services/modules/contact";
import handleRole from "@/services/modules/role";
import handleInventory from "@/services/modules/inventory";
import { useAsyncDropdownHelper } from "@/services/utils/asyncDropdownHelper";

import TitleButton from "@/components/atom/TitleButton";
import Loader from "@/components/atom/LoaderComponent";
import DateQuerySetter from "@/components/atom/DateQuerySetter";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import ContactDropdownFilter from "@/components/atom/Contact/ContactDropdownFilter.vue";
import FilterModal from "@/components/atom/FilterModal.vue";
import ProfitLossAnalyticsTable from "@/components/molecule/company/balance-sheet/ProfitLossAnalyticsTable.vue";

export default {
    name: "ProfitLossAnalytics",
    components: {
        ProfitLossAnalyticsTable,
        FilterModal,
        ContactDropdownFilter,
        AsyncSelect,
        TitleButton,
        DateQuerySetter,
        Loader
    },

    data: () => ({
        businesses: [],
        projects: [],
        periods: [],
        columns: [
            {
                name: "Period" ,
                key: "period",
            },
            {
                name: "Business Unit",
                key: "business",
            },
            {
                name: "Cost Centre",
                key: "cost_centre",
            },
            {
                name: "Project",
                key: "project",
            }
        ],
        costCentres: [],
        period: "monthly",
        columnBy: "period",
        projectId: null,
        costCentreId: null,
        businessId: null,
        contactProfile: null,
        contactProfiles: [],
        companyRoles: [],
        profitLossData: []
    }),

    computed: {
        token() {
            return localStorage.getItem('token')
        },
        companyId() {
            return this.$route.params.companyId;
        },
        start() {
            return this.$route.query.start;
        },
        end() {
            return this.$route.query.end;
        },
        revenue() {
            return this.profitLossData && this.profitLossData.revenue ? this.profitLossData.revenue : [];
        },
        costOfSales() {
            return this.profitLossData && this.profitLossData.cost_of_sales ? this.profitLossData.cost_of_sales : [];
        },
        operatingExpenses() {
            return this.profitLossData && this.profitLossData.operating_expenses ? this.profitLossData.operating_expenses : [];
        },
        otherIncome() {
            return this.profitLossData && this.profitLossData.other_income ? this.profitLossData.other_income : [];
        },
        otherExpense() {
            return this.profitLossData && this.profitLossData.other_expenses ? this.profitLossData.other_expenses : [];
        },
        reportPeriod() {
            return this.profitLossData && this.profitLossData.columns ? this.profitLossData.columns : [];
        },
        grossProfit() {
            return this.getTotal(this.revenue) - this.getTotal(this.costOfSales);
        },
        operatingProfit() {
            return this.grossProfit - this.getTotal(this.operatingExpenses);
        },
        netProfit() {
            return (
                this.operatingProfit +
                this.getTotal(this.otherIncome) -
                this.getTotal(this.otherExpense)
            );
        },
        noOfDecimals() {
            const val = parseInt(this.$route.query.decimals);
            return isNaN(val) ? 2 : val;
        },
        isModalOpen() {
            return this.$store.state.isModalOpen;
        },
        showAccountCode() {
            if (this.$route.query.show_account_code === undefined) return false;
            return !!parseInt(this.$route.query.show_account_code);
        },
        columnGrossProfit() {
            return this.computePeriodDiff(this.revenue, this.costOfSales);
        },
        columnOperatingProfit() {
            const gp = this.computePeriodDiff(this.revenue, this.costOfSales);
            const result = {};
            (this.reportPeriod || []).forEach(p => {
                const key = p.key;
                result[key] = (gp[key] || 0) - this.computePeriodTotal(this.operatingExpenses, key);
            });
            return result;
        },
        columnNetProfit() {
            const op = this.columnOperatingProfit;
            const result = {};
            (this.reportPeriod || []).forEach(p => {
                const key = p.key;
                result[key] =
                    (op[key] || 0) +
                    this.computePeriodTotal(this.otherIncome, key) -
                    this.computePeriodTotal(this.otherExpense, key);
            });
            return result;
        }
    },

    methods: {
        exportProfitLoss() {
            // Working on export
            let url = `${process.env.VUE_APP_BASE_URL}/export/accounting/profit-and-loss-analytics` + this.getQuery() + `&_token=${this.token}`;
            let a = document.createElement("a")
            a.setAttribute("href", url)
            a.click()
        },

        getTotal(data) {
            return Array.isArray(data)
                ? data.reduce((sum, i) => sum + (i.total || 0), 0)
                : 0;
        },

        computePeriodTotal(nodes, key) {
            if (!Array.isArray(nodes)) return 0;
            return nodes.reduce((sum, node) => {
                let val = (node.columns && node.columns[key]) || 0;
                if (Array.isArray(node.child) && node.child.length)
                    val += this.computePeriodTotal(node.child, key);
                return sum + val;
            }, 0);
        },

        computePeriodDiff(positive, negative) {
            const result = {};
            if (!Array.isArray(this.reportPeriod)) return result;
            this.reportPeriod.forEach(p => {
                const key = p.key;
                const pos = this.computePeriodTotal(positive, key);
                const neg = this.computePeriodTotal(negative, key);
                result[key] = pos - neg;
            });
            return result;
        },

        onClickGo(filter) {
            this.getDateWiseProfitLoss(this.getQuery(filter));
        },

        getShowAccountCode(filter, routeQuery) {
            if (filter && filter.showAccountCode !== undefined) return !!parseInt(filter.showAccountCode)
            if (routeQuery.show_account_code === undefined) return false
            return !!parseInt(routeQuery.show_account_code)
        },

        accountCodeParam(showAccountCode) {
            if (!showAccountCode) return undefined
            return 1
        },

        getQuery(filter) {
            const routeQuery = Object.assign({}, this.$route.query);
            const decimals = filter ? filter.decimals : routeQuery.decimals;
            const showNonZero = filter
                ? filter.showNonZero
                : (routeQuery.show_non_zero || 1);
            const showAccountCode = this.getShowAccountCode(filter, routeQuery);
            const start = !this.start ? routeQuery.start_date : this.start;
            const end = !this.end ? routeQuery.end_date : this.end;

            const params = {
                company_id: this.companyId,
                start_date: start,
                end_date: end,
                business_id: this.businessId || undefined,
                project_id: this.projectId || undefined,
                cost_centre_id: this.costCentreId || undefined,
                contact_profile_id: this.contactProfile && this.contactProfile.id ? this.contactProfile.id : undefined,
                decimals,
                period: (this.columnBy === 'period' && this.period) ? this.period : undefined,
                column_by: this.columnBy,
                show_non_zero: showNonZero,
                show_account_code: this.accountCodeParam(showAccountCode)
            };

            Object.keys(params).forEach(key => {
                if (params[key] == null) delete params[key];
            });

            const query = `?${new URLSearchParams(params).toString()}`;
            delete params.start_date;
            delete params.end_date;
            params.start = start;
            params.end = end;
            this.$router.push({ path: this.$route.path, query: params });
            return query;
        },

        async getDateWiseProfitLoss(query = "") {
            try {
                this.accountingLoading = true;
                const res = await this.getProfitLossAnalytics(query);
                if (res && res.status) {
                    this.profitLossData = res.data;
                } else {
                    const message = res && res.response && res.response.message
                        ? res.response.message
                        : "Failed to load data";
                    this.showError(message);
                }
            } catch (err) {
                const msg = err && err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : "Network error";
                this.showError(msg);
            } finally {
                this.accountingLoading = false;
            }
        },

        async getBusinesses() {
            const res = await this.fetchBusinessList(
                `?company_id=${this.$route.params.companyId}`
            );
            if (res && res.status) this.businesses = res.data;
        },

        async getContactProfiles() {
            const companyId = this.$route.params.companyId;
            const res = await this.fetchContactProfiles(`?company_id=${companyId}`);
            if (res && res.status) this.contactProfiles = res.data;
        },

        onOpenContactSearchModal() {
            this.$store.state.isModalOpenThree = true;
        },

        async getCompanyRoles() {
            const res = await this.fetchCompanyDefaultRoles(
                `?company_id=${this.$route.params.companyId}`
            );
            if (res && res.status) this.companyRoles = res.data;
        },

        onSearchContact(contact) {
            this.contactProfile = contact;
        },

        onClickFilterButton() {
            this.$store.state.isModalOpen = true;
        },

        onFilter(filter) {
            this.onClickGo(filter);
        }
    },

    setup() {
        const showError = inject("showError");
        const showSuccess = inject("showSuccess");
        const store = useStore();

        const { accountingLoading, getProfitLossAnalytics } = handleAccounting();
        const { commaFormat } = figureFormatter();
        const { fetchBusinessList } = handleCBusinesses();
        const { fetchProjects } = handleProjects();
        const { fetchCostCentreList } = handleCostCentres();
        const { fetchContactProfiles } = handleContact();
        const { formatPatientLabel } = useAsyncDropdownHelper();
        const { fetchCompanyDefaultRoles } = handleRole();
        const { fetchHome } = handleInventory();

        return {
            fetchHome,
            getProfitLossAnalytics,
            fetchBusinessList,
            fetchProjects,
            fetchCostCentreList,
            fetchContactProfiles,
            fetchCompanyDefaultRoles,
            accountingLoading,
            commaFormat,
            store,
            showError,
            showSuccess,
            formatPatientLabel
        };
    },

    mounted() {
        this.getDateWiseProfitLoss(this.getQuery());
        this.getBusinesses();

        const companyQuery = `?company_id=${this.$route.params.companyId}`;

        Promise.all([
            this.fetchProjects(companyQuery).then(res => {
                if (res && res.data) this.projects = res.data;
            }),
            this.fetchCostCentreList(companyQuery).then(res => {
                if (res && res.data) this.costCentres = res.data;
            }),
            this.getCompanyRoles(),
            this.fetchHome().then(res => {
                if (res && res.data) {
                    const intervals = res.data.project_intervals || {};
                    const periods = [];
                    Object.keys(intervals).forEach(key => {
                        if (!["half_yearly", "one_off"].includes(key)) {
                            periods.push({ key, name: intervals[key], label: intervals[key] });
                        }
                    });
                    this.periods = periods;
                }
            })
        ])
            .catch(err => this.showError(err.message))
            .finally(() => {
                this.isDataLoading = false;
            });

        this.getContactProfiles();
    }
};
</script>
<style scoped>
    .profit-loss-table th,
    .profit-loss-table td {
        white-space: nowrap;
    }
</style>

